// apps/widget/src/lib/i18n.ts
//
// Five locales: en, es, de, bn, ar. Bangla matters because Bangladesh is a
// primary market, and a scan flow in English there converts a fraction as
// well. Arabic is written right to left: the whole widget mirrors for it.

export type Locale = 'en' | 'es' | 'de' | 'bn' | 'ar';

export const LOCALES: Locale[] = ['en', 'es', 'de', 'bn', 'ar'];

const RTL: Locale[] = ['ar'];

type Dictionary = Record<string, string>;

const cache = new Map<Locale, Dictionary>();
let current: Locale = 'en';
let dictionary: Dictionary = {};
const listeners = new Set<() => void>();

const loaders: Record<Locale, () => Promise<{ default: Dictionary }>> = {
  en: () => import('../locales/en.json'),
  es: () => import('../locales/es.json'),
  de: () => import('../locales/de.json'),
  bn: () => import('../locales/bn.json'),
  ar: () => import('../locales/ar.json'),
};

export async function setLocale(locale: Locale): Promise<void> {
  const resolved = LOCALES.includes(locale) ? locale : 'en';

  if (!cache.has(resolved)) {
    const module = await loaders[resolved]();
    cache.set(resolved, module.default);
  }

  current = resolved;
  dictionary = cache.get(resolved)!;

  document.documentElement.lang = resolved;
  document.documentElement.dir = isRtl(resolved) ? 'rtl' : 'ltr';

  for (const listener of listeners) listener();
}

export function getLocale(): Locale {
  return current;
}

export function isRtl(locale: Locale = current): boolean {
  return RTL.includes(locale);
}

/**
 * Who the words are addressed to. The report is written to the patient —
 * "Your skin report", "See my treatment plan". Clinic staff opening the same
 * report from their dashboard read it about the patient instead: a key's
 * `:staff` variant is used when one exists, and the patient's wording is kept
 * for everything that reads the same either way.
 */
export type Voice = 'patient' | 'staff';
let voice: Voice = 'patient';

export function setVoice(next: Voice): void {
  voice = next;
}

/**
 * Missing keys fall back to the key itself rather than blank. A visible
 * "report.title" in a translation review is far more useful than empty space
 * nobody notices.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  let value = (voice === 'staff' ? dictionary[`${key}:staff`] : undefined) ?? dictionary[key] ?? key;
  if (vars) {
    for (const [name, replacement] of Object.entries(vars)) {
      value = value.replace(new RegExp(`\\{${name}\\}`, 'g'), String(replacement));
    }
  }
  return value;
}

export function onLocaleChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const CHOSEN_KEY = 'gs.locale';

/** What a language calls itself — the switcher lists each in its own words. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  bn: 'বাংলা',
  ar: 'العربية',
};

/**
 * The visitor's own earlier pick in the switcher, then the clinic's default
 * language — the clinic chose it for its patients, so a browser set to
 * English does not overrule it. The browser's language only decides when the
 * clinic's default is not one it offers.
 */
export function detectLocale(available: string[], tenantDefault: string): Locale {
  const supported = available.filter((l): l is Locale => LOCALES.includes(l as Locale));

  let chosen: string | null = null;
  try {
    chosen = localStorage.getItem(CHOSEN_KEY);
  } catch {
    /* Storage blocked inside some third-party frames; the clinic decides. */
  }
  if (chosen && supported.includes(chosen as Locale)) return chosen as Locale;

  const fallback = tenantDefault as Locale;
  if (supported.includes(fallback)) return fallback;

  const browser = navigator.language.slice(0, 2) as Locale;
  if (supported.includes(browser)) return browser;
  return supported[0] ?? (LOCALES.includes(fallback) ? fallback : 'en');
}

/** A language picked in the widget's own switcher, remembered for next time. */
export async function chooseLocale(locale: Locale): Promise<void> {
  await setLocale(locale);
  try {
    localStorage.setItem(CHOSEN_KEY, locale);
  } catch {
    /* Remembered for this visit only. */
  }
}

/** A metric's name in the visitor's language; the server's English if there is none. */
export function metricLabel(key: string, fallback: string): string {
  const label = t(`metric.${key}`);
  return label === `metric.${key}` ? fallback : label;
}

/**
 * The locale for Intl formatting. Arabic keeps Western digits, like the
 * scores and ages the widget shows as plain numbers — one set of digits on
 * the screen, not two.
 */
export function intlLocale(): string {
  return current === 'ar' ? 'ar-u-nu-latn' : current;
}

/** Prices render in the clinic's currency, in the visitor's number format. */
export function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}
